import CustomAvatar from '@/components/CustomAvatar';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { parseToPortableText } from '@/components/TextEditor/parseToPortableText';
import { TextEditorInitialValueType } from '@/components/TextEditor/TextEditor.types';
import Modal from '@/widgets/Modal';
import { EditorState } from 'draft-js';
import React, { ForwardedRef, forwardRef, useMemo } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import ErrorMessage from '@/domains/AuthModal/components/ErrorMessage';
import xw from 'xwind';
import { capitalize } from '@/utils/index';
import {
  CharCounter,
  Editor,
  Footer,
  Header,
  InputWrapper,
  ModalWrapper,
  Select,
  TitleInput,
  Tooltip,
  PostButton,
  PostError,
} from './PostDiscussionModal.styles';
import {
  PostDiscussionModalPropTypes,
  SelectPropTypes,
  TextEditorPropTypes,
  TitleInputPropTypes,
} from './PostDiscussion.types';

const DEFAULT_TITLE_INPUT_PROPS: TitleInputPropTypes = {
  placeholder: '',
  hide: false,
  initialValue: '',
  maxLength: 60,
  onChange: null,
};

const DEFAULT_SELECT_PROPS: SelectPropTypes = {
  options: [],
  hide: false,
  initialValue: '',
  onChange: null,
  placeholder: '',
};

const DEFAULT_TEXT_EDITOR_PROPS: TextEditorPropTypes = {
  initialValue: null,
  placeholder: '',
  onChange: null,
};

function useGetDefaultObjectProps(props: any, defaultProps: any) {
  return useMemo(() => ({ ...defaultProps, ...props }), [props, defaultProps]);
}

function initializeEditor(initialValue: TextEditorInitialValueType) {
  return () => {
    if (initialValue?.contentState) {
      const { contentState, imageMap } = initialValue;
      return {
        editorState: EditorState?.createWithContent(contentState),
        imageMap,
      };
    }
    return {
      editorState: EditorState.createEmpty(),
      imageMap: null,
    };
  };
}

function initializeSelect(selectProps: SelectPropTypes) {
  if (selectProps?.hide) return null;
  const [currentGroup] =
    selectProps?.options?.filter(
      (option) => option?.value === selectProps?.initialValue,
    ) ?? [];
  const { value, label } = currentGroup || {};
  return currentGroup ? { value, label } : null;
}

export const PostDiscussionModal = forwardRef(
  (
    {
      isOpen,
      onSave,
      handleClose,
      user,
      loading,
      selectProps,
      titleInputProps,
      textEditorProps,
      noGroup,
    }: PostDiscussionModalPropTypes,
    ref: ForwardedRef<HTMLDivElement>,
  ): JSX.Element => {
    const editorProps = useGetDefaultObjectProps(
      textEditorProps,
      DEFAULT_TEXT_EDITOR_PROPS,
    );
    const titleProps = useGetDefaultObjectProps(
      titleInputProps,
      DEFAULT_TITLE_INPUT_PROPS,
    );
    const selectionProps = useGetDefaultObjectProps(
      selectProps,
      DEFAULT_SELECT_PROPS,
    );

    const validationSchema = Yup.object().shape({
      userId: Yup.string(),
      title: !noGroup
        ? Yup.string().trim().max(60).required('Title required')
        : Yup.string(),
      editor: Yup.object().test({
        name: 'editor',
        message: 'Content required',
        test: (value) => parseToPortableText(value.editorState).length > 0,
      }),
      selectedOption: !noGroup
        ? Yup.object().required('Group required')
        : Yup.object(),
    });

    const initialValues = useMemo(
      () => ({
        userId: user?.id,
        title: '',
        editor: initializeEditor(editorProps?.initialValue)(),
        selectedOption: initializeSelect(selectProps) || '',
      }),
      [user?.id, editorProps?.initialValue, selectProps],
    );

    const handleSubmit = async (values) => {
      const { title, editor, userId } = values;
      const sanityBlocks = parseToPortableText(editor.editorState);
      await onSave({
        title,
        userId,
        body: sanityBlocks,
        imageMap: editor.imageMap,
        selectedOption: values.selectedOption?.value,
      });
      handleClose();
    };

    return (
      <Modal
        isOpen={isOpen}
        withCloseButton
        useOutsideCloseButton
        handleClose={handleClose}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          // validationSchema={validationSchema}
        >
          {(props) => (
            <ModalWrapper ref={ref}>
              <Header>
                <CustomAvatar
                  size={{ desktop: 20, mobile: 11 }}
                  fontSize={{ desktop: 10, mobile: 5 }}
                  name={user?.firstName}
                  image={user?.image?.url}
                />
                {!selectionProps?.hide && (
                  <Field name="selectedOption">
                    {({ field, meta }) => (
                      <>
                        <Select
                          options={selectionProps?.options}
                          placeholder={selectionProps?.placeholder}
                          {...field}
                          onChange={(value) =>
                            field.onChange({
                              type: 'change',
                              target: {
                                name: 'selectedOption',
                                value,
                              },
                            })
                          }
                        />
                        <Tooltip
                          message="Choose where you want to share your thoughts and inspire others 😃"
                          TooltipIcon={InfoIcon}
                          placement="top-end"
                          height={68}
                          width={350}
                        />

                        {meta.touched && meta.error && (
                          <PostError>
                            <ErrorMessage css={xw`mt-1 sm:mt-2`}>
                              {capitalize(meta.error)}
                              {meta.initialError}
                            </ErrorMessage>
                          </PostError>
                        )}
                      </>
                    )}
                  </Field>
                )}
              </Header>
              {!titleInputProps?.hide && (
                <Field name="title">
                  {({ field, meta }) => (
                    <>
                      <InputWrapper>
                        <TitleInput
                          inputProps={{ maxLength: titleProps?.maxLength }}
                          placeholder={titleProps?.placeholder}
                          {...field}
                        />
                        <CharCounter>
                          {field.value.length.toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                          })}
                          /{titleProps?.maxLength}
                        </CharCounter>
                      </InputWrapper>
                      {meta.touched && meta.error && (
                        <PostError>
                          <ErrorMessage css={xw`-mb-3 -mt-3 sm:mb-4`}>
                            {capitalize(meta.error)}
                            {meta.initialError}
                          </ErrorMessage>
                        </PostError>
                      )}
                    </>
                  )}
                </Field>
              )}

              <Field name="editor">
                {({ field, meta }) => (
                  <>
                    <Editor
                      maxHeight={{ desktop: 500, mobile: 320 }}
                      minHeight={{ desktop: 310, mobile: 320 }}
                      placeholder={editorProps?.placeholder}
                      {...field}
                      onChange={(value) => {
                        editorProps?.onChange?.(value);

                        return field.onChange({
                          type: 'change',
                          target: {
                            name: 'editor',
                            value,
                          },
                        });
                      }}
                    />
                    {meta.touched && meta.error && (
                      <PostError>
                        <ErrorMessage css={xw`-mt-3 sm:mt-2`}>
                          {capitalize(meta.error)}
                          {meta.initialError}
                        </ErrorMessage>
                      </PostError>
                    )}
                  </>
                )}
              </Field>
              <Footer>
                {!selectionProps?.hide && (
                  <Field name="selectedOption">
                    {({ field, meta }) => (
                      <>
                        <Select
                          options={selectionProps?.options}
                          placeholder={selectionProps?.placeholder}
                          {...field}
                          onChange={(value) =>
                            field.onChange({
                              type: 'change',
                              target: {
                                name: 'selectedOption',
                                value,
                              },
                            })
                          }
                        />
                        <Tooltip
                          message="Choose where you want to share your thoughts and inspire others 😃"
                          TooltipIcon={InfoIcon}
                          placement="top-end"
                          height={68}
                          width={350}
                        />

                        {meta.touched && meta.error && (
                          <PostError css={xw`absolute top-full left-0`}>
                            <ErrorMessage css={xw`mt-1 sm:mt-2`}>
                              {capitalize(meta.error)}
                              {meta.initialError}
                            </ErrorMessage>
                          </PostError>
                        )}
                      </>
                    )}
                  </Field>
                )}
                <PostButton loading={loading} onClick={props.submitForm}>
                  Post
                </PostButton>
              </Footer>
            </ModalWrapper>
          )}
        </Formik>
      </Modal>
    );
  },
);
